import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, ClockIcon, FrownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldAlertIcon, ShieldIcon, SparklesIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, faqs, mockupUrl, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-bold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent'}`} data-id="element-17" data-mp="1nqgzbo9kccil">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-extrabold leading-tight text-slate-900 md:text-4xl" data-id="element-18" data-mp="3411hfoncof0q">
      {children}
    </h2>;
}
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border border-sky-100 bg-white p-6 shadow-sm" data-id="element-19" data-mp="16rdlfdcsz25m">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/20" data-id="element-20" data-mp="12r3cpsxmlxn2" />
      <div className="mb-4 text-sm text-gold" data-id="element-21" data-mp="xpdy63b731u8">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-22" data-mp="srcjrvoprqp3">
        “{item.text}”
      </p>
      <div className="flex items-center gap-3 border-t border-sky-100 pt-4" data-id="element-23" data-mp="nzaxdgxu0jlc">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/25 bg-sky-50 font-display text-sm font-bold text-accent" data-id="element-24" data-mp="j083msnaxzso">
          {item.initials}
        </span>
        <div data-id="element-25" data-mp="9z9yu851ho91">
          <p className="text-sm font-bold text-slate-900" data-id="element-26" data-mp="9zs2hpu174qz">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-27" data-mp="50yozxeqvdqt">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-14 md:py-16" data-id="element-28" data-mp="3rl3dwwg9zzyf">
      <Eyebrow data-id="element-29" data-mp="dh3mkz1wz8l">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-30" data-mp="3hl2r30j771ph">
        Personas reales que ya dejaron atrás <span className="text-accent" data-id="element-31" data-mp="3hmniwie4o6jn">el dolor de espalda</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-32" data-mp="3mkp69fil6s0x">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-33" data-mp="e4vxxnyesmuy" />)}
      </div>
      <div className="mx-auto mt-8 max-w-3xl rounded-3xl border-2 border-sky-200 bg-white p-6 text-center shadow-lg shadow-sky-100" data-id="element-34" data-mp="j1vtv61jo98c">
        <p className="text-sm font-bold uppercase tracking-wide text-accent" data-id="element-35" data-mp="4c7zypvp0cun">
          ⏰ Oferta de lanzamiento por tiempo limitado
        </p>
        <p className="mt-2 text-xl font-extrabold text-slate-900" data-id="element-36" data-mp="x2q1vnkok86h">
          Sumate hoy y empezá a cuidar tu espalda{' '}
          <span className="text-accent" data-id="element-37" data-mp="11ugfc2ewzc24">en menos de 15 minutos por día.</span>
        </p>
        <div className="mt-5" data-id="element-38" data-mp="2mj2789ywednv">
          <Button href="#pricing" data-id="element-39" data-mp="2mi0rvtaun50c">QUIERO VER MI OFERTA DEL COMBO ESPALDA Y COLUMNA</Button>
        </div>
      </div>
    </section>;
}
const pains = ['Buscás ejercicios en internet y no sabés si te hacen bien o te empeoran.', 'Empezás rutinas que abandonás porque no ves progresión.', 'El dolor vuelve siempre al mismo lugar porque nunca trabajaste la causa real.', 'Pasás horas sentado sin saber cómo compensar el daño postural acumulado.', 'Tenés miedo de lesionarte más si hacés ejercicio sin guía clara.', 'Vivís con analgésicos que tapan el síntoma pero no resuelven nada.'];
const transformations = ['Abrís el tomo y sabés qué hacer hoy', 'Movilidad, fuerza, postura y estabilidad', 'Rutinas para casa o gimnasio', 'Progresión real semana a semana'];
export function PainAndTransformation() {
  return <>
      <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-40" data-mp="3k00vjh9op4y7">
        <div className="mx-auto max-w-4xl" data-id="element-41" data-mp="j4jmmwk86d2h">
          <Eyebrow danger data-id="element-42" data-mp="j5l8lw2s4jyz">😤 La realidad</Eyebrow>
          <SectionTitle data-id="element-43" data-mp="n5vhphlvbqi2">
            ¿Cuánto tiempo más vas a convivir con el dolor de espalda{' '}
            <span className="text-accent" data-id="element-44" data-mp="s4ydebfykkdd">sin hacer nada real?</span>
          </SectionTitle>
          <p className="mx-auto mt-6 max-w-3xl text-center font-medium text-slate-600" data-id="element-45" data-mp="x41fbn0n6ah4">
            No es que no te importe tu cuerpo. Es que nadie te dio un sistema:
          </p>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 md:gap-4" data-id="element-46" data-mp="1211lrz1elf8r">
            {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/60 p-4" data-id="element-47" data-mp="162d2tg67os5c">
                <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-48" data-mp="1ft7tcwqhqdwl" />
                <span className="text-sm font-medium text-slate-700 md:text-base" data-id="element-49" data-mp="1kuf1wy0gjh4b">{pain}</span>
              </div>)}
          </div>
          <div className="mt-10 rounded-2xl border border-primary/20 bg-sky-50 p-6 text-center" data-id="element-50" data-mp="1pqm7uqsussm9">
            <p className="text-lg font-bold text-slate-700 md:text-2xl" data-id="element-51" data-mp="3a8obejqucz92">
              No es falta de voluntad.
              <br data-id="element-52" data-mp="35e0v385zrb5k" />
              <span className="text-xl text-accent md:text-3xl" data-id="element-53" data-mp="30cbhbkrdwjds">Es falta de sistema.</span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-54" data-mp="2vf1mtqp51hj3">
        <Eyebrow data-id="element-55" data-mp="3t1iiysm4xp39">
          <span className="inline-flex items-center gap-2" data-id="element-56" data-mp="3szxo11vws44v">
            <SparklesIcon className="h-4 w-4" data-id="element-57" data-mp="3o2fou37p13a2" /> La transformación
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-58" data-mp="3jb60d5xvs5zr">
          ¿Y si mañana te levantaras sabiendo exactamente{' '}
          <span className="text-accent" data-id="element-59" data-mp="3ecciz2fh3uub">qué hacer con tu espalda?</span>
        </SectionTitle>
        <div className="mt-8 rounded-3xl border border-primary/20 bg-sky-50/70 p-6 shadow-[0_10px_40px_rgba(14,165,233,.12)] md:p-10" data-id="element-60" data-mp="fmcefpp750i1">
          <p className="text-center text-base font-bold leading-relaxed text-slate-900 md:text-xl" data-id="element-61" data-mp="avdtgct9je8g">
            Abrís el tomo, seguís el protocolo y empezás a moverte con confianza. Sin dudar si el
            ejercicio te sirve o te empeora.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-62" data-mp="2a4drbhrfh1ir">
            {transformations.map(item => <div key={item} className="rounded-xl border border-primary/20 bg-white p-3 text-center text-xs font-bold text-accent md:text-sm" data-id="element-63" data-mp="20chvkdctdnhp">
                {item}
              </div>)}
          </div>
          <p className="mt-6 text-center font-medium text-slate-600" data-id="element-64" data-mp="1qch6eas9kd5l">
            Eso es el{' '}
            <strong className="text-slate-900" data-id="element-65" data-mp="1cgnacscl9i9w">Combo Completo Espalda y Columna 2026.</strong>
          </p>
        </div>
        <div className="mt-8 text-center" data-id="element-66" data-mp="36stvaivabdfe">
          <Button href="#pricing" data-id="element-67" data-mp="36tv92v3oqe0t">QUIERO EMPEZAR HOY</Button>
        </div>
      </section>
    </>;
}
const values = ['Más de 200 ejercicios explicados', '5 Tomos completos', 'De principiante a intermedio', 'Rutinas listas para aplicar', 'Para casa o gimnasio', 'Adaptado para Argentina'];
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-16" data-id="element-68" data-mp="19umg0qtxp7hv">
      <Eyebrow data-id="element-69" data-mp="153nzbq51a7e3">
        <span className="inline-flex items-center gap-2" data-id="element-70" data-mp="1534h08bh1gzj">
          <TrophyIcon className="h-4 w-4" data-id="element-71" data-mp="151kzyfhzr1wp" /> El sistema Espalda y Columna 2026
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-72" data-mp="1ix5fr22fb3zw">
        5 tomos. <span className="text-accent" data-id="element-73" data-mp="1dwhi3xjovowp">Toda tu columna cubierta.</span>
      </SectionTitle>
      <p className="mt-4 text-center font-medium text-slate-600" data-id="element-74" data-mp="1ssyt2nnyv1iq">
        De la movilidad básica al entrenamiento completo. Todo listo para empezar hoy.
      </p>

      <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3" data-id="element-75" data-mp="1nux79uz5oo3k">
        {values.map(value => <div key={value} className="flex items-center gap-3 rounded-xl border border-sky-100 bg-white p-4 shadow-sm" data-id="element-76" data-mp="3umppjbleo3hl">
            <span className="rounded-lg bg-sky-100 p-2" data-id="element-77" data-mp="33t4mbtzt2bss">
              <CheckIcon className="h-4 w-4 text-accent" data-id="element-78" data-mp="33qifxxoni2c5" />
            </span>
            <span className="text-sm font-bold leading-tight text-slate-800" data-id="element-79" data-mp="38plfj2lv3gcq">{value}</span>
          </div>)}
      </div>

      <div className="mt-16" data-id="element-80" data-mp="3ih98x91zb7gu">
        <h3 className="text-center text-2xl font-extrabold text-slate-900 md:text-4xl" data-id="element-81" data-mp="3ihir4ags8abz">
          Todo lo que necesitás para{' '}
          <span className="text-accent" data-id="element-82" data-mp="2kv1y3hf30iw1">tener una espalda fuerte y sin dolor.</span>
        </h3>
        <p className="mt-3 text-center text-sm font-medium text-slate-500" data-id="element-83" data-mp="2pt3n0iz6vcff">
          Tocá un tomo para ver el contenido
        </p>
        <div className="mt-8 space-y-3" data-id="element-84" data-mp="2yt9yy0f8o4ch">
          {tomos.map((tomo, index) => <div key={tomo.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors ${open === index ? 'border-primary shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-85" data-mp="2yr5m2szd3v02" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-86" data-mp="1yktp32npx5zj">
                <span className="flex items-center gap-3" data-id="element-87" data-mp="1pqnfa6wzdyhl">
                  <span className="text-2xl" data-id="element-88" data-mp="1ksm0hy6l8lbx">{tomo.icon}</span>
                  <span data-id="element-89" data-mp="1fuc27epkh4m3">
                    <b className="block text-slate-900" data-id="element-90" data-mp="1fuu3wqv00b1e">
                      Tomo {index + 1} — {tomo.name}
                    </b>
                    <small className="font-bold text-accent" data-id="element-91" data-mp="1au64bgspelvk">{tomo.period}</small>
                  </span>
                </span>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-92" data-mp="11usuuo4kxxau" />
              </button>
              <AnimatePresence initial={false} data-id="element-93" data-mp="315kickq02o7y">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} data-id="element-94" data-mp="2w6r2wmr5kn55">
                    <ul className="space-y-2 border-t border-sky-100 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-95" data-mp="2e5ufm9jwqw7k">
                      {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-96" data-mp="294n88blofz1f">
                          <span className="text-accent" data-id="element-97" data-mp="1lhynli5a9sh4">→</span>
                          {line}
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>

      <MidPageCta data-id="element-98" data-mp="1qfgqorwmddh7" />
    </section>;
}
function MidPageCta() {
  return <div className="mt-14 overflow-hidden rounded-3xl border-2 border-primary bg-sky-50 p-6 shadow-lg shadow-sky-100 md:p-9" data-id="element-99" data-mp="12pw1vsdsbm00">
      <div className="grid items-center gap-6 md:grid-cols-[minmax(0,240px)_1fr] md:gap-9" data-id="element-100" data-mp="xr1m6zb46dk4">
        <a href="#pricing" className="group block rounded-2xl focus:outline-none" aria-label="Ver las opciones de compra del Combo Completo Espalda y Columna 2026" data-id="element-101" data-mp="39e4vhkhorvz9">
          <img src={mockupUrl} alt="Los 5 tomos y los 5 bonos del Combo Completo Espalda y Columna 2026" className="mx-auto w-full max-w-[240px] object-contain drop-shadow-[0_18px_28px_rgba(14,165,233,.25)] transition-transform duration-300 group-hover:scale-[1.03]" data-id="element-102" data-mp="30ft59jkqf07c" />
        </a>
        <div className="text-center md:text-left" data-id="element-103" data-mp="x0htoof0b02">
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-accent" data-id="element-104" data-mp="3j3odknxkrlt2">
            🔥 No esperes al próximo lunes
          </p>
          <p className="mt-3 text-xl font-extrabold leading-tight text-slate-900 md:text-3xl" data-id="element-105" data-mp="3o5x83oq3ivxa">
            Cada semana sin sistema es otra semana de dolor.{' '}
            <span className="text-accent" data-id="element-106" data-mp="2cpgp5lfc1x5o">Empezá tu primera rutina hoy.</span>
          </p>
          <p className="mt-3 text-sm font-medium text-slate-600 md:text-base" data-id="element-107" data-mp="1slhroi9f16cz">
            5 Tomos + 5 Bonos por menos de lo que gastás en una sola sesión de kinesiología.
          </p>
          <div className="mt-6" data-id="element-108" data-mp="1jtpp0tqmv1iy">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-109" data-mp="1jsoc0jq244yl">
              <span className="inline-flex items-center gap-2" data-id="element-110" data-mp="1euczg18dngco">
                DESBLOQUEAR MI PROGRAMA COMPLETO AHORA
                <ArrowRightIcon className="h-4 w-4" data-id="element-111" data-mp="19xcvpwz02poq" />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
const beforeAfterRows = [['Buscabas ejercicios sueltos en internet sin saber si servían', 'Abrís el tomo, seguís el protocolo y sabés por qué lo hacés'], ['Empezabas rutinas que abandonabas a las dos semanas', 'Tabla de progresión de 12 semanas con criterios de avance'], ['El dolor volvía siempre al mismo lugar', 'Trabajás la causa real: movilidad, core y postura'], ['Horas sentado sin compensar el daño postural', 'Pausas activas y rutinas post-jornada listas para usar'], ['Miedo a lesionarte más al entrenar sin guía', 'Sistema de Semáforo para entrenar seguro incluso con molestia'], ['Analgésicos que solo tapaban el síntoma', 'Una espalda más fuerte y estable que sostiene el resultado']];
export function BeforeAfterAndBonuses() {
  return <>
      <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-112" data-mp="2ewt0jug2xw1j">
        <div className="mx-auto max-w-5xl" data-id="element-113" data-mp="2jul09amyumga">
          <Eyebrow data-id="element-114" data-mp="2ot5qqicf1rrj">🔄 Antes vs. después</Eyebrow>
          <SectionTitle data-id="element-115" data-mp="2osctc7zckem2">
            No comprás archivos. <span className="text-accent" data-id="element-116" data-mp="2surbepv6zaca">Comprás un sistema.</span>
          </SectionTitle>
          <div className="mt-10 grid gap-4 md:grid-cols-2 md:gap-6" data-id="element-117" data-mp="2sselnqj6zvoo">
            <div className="rounded-2xl border border-red-200 bg-red-50/40 p-6" data-id="element-118" data-mp="42dpoapc4rp9">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-119" data-mp="8379arvqvkcc">
                <XIcon className="rounded-lg bg-red-100 p-2 text-red-500" size={40} data-id="element-120" data-mp="6mjsug64ke4r" />
                Antes — Sin sistema
              </h3>
              {beforeAfterRows.map(([before]) => <p key={before} className="mb-3 flex gap-2 text-sm font-medium text-slate-600" data-id="element-121" data-mp="glsrvnfukixz">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-122" data-mp="bm6ene00r2p6" />
                  {before}
                </p>)}
            </div>
            <div className="rounded-2xl border-2 border-primary bg-white p-6 shadow-lg shadow-sky-100" data-id="element-123" data-mp="pk4e7d9okukm">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-124" data-mp="kl9xqhz6ri3o">
                <CheckIcon className="rounded-lg bg-sky-100 p-2 text-accent" size={40} data-id="element-125" data-mp="ze3ee6q7249x" />
                Después — Con el Combo Espalda y Columna
              </h3>
              {beforeAfterRows.map(([, after]) => <p key={after} className="mb-3 flex gap-2 text-sm font-bold text-slate-800" data-id="element-126" data-mp="18achbvddrl0t">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" data-id="element-127" data-mp="149adt2cfkmt1" />
                  {after}
                </p>)}
            </div>
          </div>
          <p className="mt-10 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-128" data-mp="1rxytxvdsc21u">
            Recuperás <span className="text-accent" data-id="element-129" data-mp="1dy7m6cdv3hwa">movilidad, claridad</span> y la seguridad de
            moverte sin miedo.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:py-16" data-id="element-130" data-mp="148eazchmt4sk">
        <Eyebrow data-id="element-131" data-mp="14733p2oz9ewp">
          <span className="inline-flex items-center gap-2" data-id="element-132" data-mp="18zcsgfecw8xe">
            <GiftIcon className="h-4 w-4" data-id="element-133" data-mp="190xo68cepxwu" /> Bonos exclusivos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-134" data-mp="va2l5colab16">
          Además te llevás <span className="text-accent" data-id="element-135" data-mp="z9jq8503t65t">5 bonos incluidos gratis</span>
        </SectionTitle>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-136" data-mp="la211oiwbqvx">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-sky-100 bg-white p-5 shadow-sm" data-id="element-137" data-mp="q7tr0czzd2xh">
              <div className="flex gap-3" data-id="element-138" data-mp="2n7u733p9iyv3">
                <span className="text-3xl" data-id="element-139" data-mp="2n612rv24gqq9">{icon}</span>
                <div data-id="element-140" data-mp="2j5h58qdmzl3l">
                  <small className="font-bold uppercase tracking-wide text-accent" data-id="element-141" data-mp="2j71ymco7sxzv">
                    Bono {number}
                  </small>
                  <h3 className="font-bold text-slate-900" data-id="element-142" data-mp="2e4t77kqzq3zv">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-143" data-mp="36vl8gwmwxiu2">
                {description}
              </p>
              <div className="flex border-t border-sky-100 pt-4 text-sm font-bold" data-id="element-144" data-mp="31vfcx5divc6p">
                <span className="text-slate-400 line-through" data-id="element-145" data-mp="2wxx3ldvleuy6">Valor: {value}</span>
                <span className="ml-auto text-accent" data-id="element-146" data-mp="1qh1syblw3gvy">GRATIS</span>
              </div>
            </article>)}
        </div>
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-primary/20 bg-sky-50 p-6 text-center" data-id="element-147" data-mp="1lh7042ik0h89">
          <p className="font-medium text-slate-600" data-id="element-148" data-mp="1bfuykzc97c8u">
            Valor total de bonos: <s className="font-bold text-slate-400" data-id="element-149" data-mp="1gclg7r3pifwd">$64.000 ARS</s>
          </p>
          <p className="mt-1 text-lg font-extrabold text-slate-900" data-id="element-150" data-mp="1lcr6p3y8pwcv">
            Hoy van <span className="text-accent" data-id="element-151" data-mp="1lamulyq6tr1i">incluidos gratis</span> con tu compra.
          </p>
          <div className="mt-6" data-id="element-152" data-mp="1q50vuhrj6se3">
            <Button href="#pricing" fullWidth className="text-xs md:text-sm" data-id="element-153" data-mp="1u5b11s7i1erw">
              QUIERO MIS 5 BONOS GRATIS →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
export function SocialAndFAQ() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-154" data-mp="2921znz9cmmgg">
      <Eyebrow data-id="element-155" data-mp="291ahpr8ocn8n">
        <span className="inline-flex items-center gap-2" data-id="element-156" data-mp="28zpnk2q9v6bb">
          <HelpCircleIcon className="h-4 w-4" data-id="element-157" data-mp="9q9dkxo3n1ii" /> Preguntas frecuentes
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-158" data-mp="eou2w21tc0s6">
        Lo que más <span className="text-accent" data-id="element-159" data-mp="eq57u56zvekv">nos preguntan</span>
      </SectionTitle>
      <div className="mt-10 space-y-3" data-id="element-160" data-mp="2gk07lzbvtphk">
        {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white ${open === index ? 'border-primary shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-161" data-mp="2blovtj2114wp" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-162" data-mp="2lctccn7coxtn">
              {question}
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-163" data-mp="26o6qe2n7x7tg" />
            </button>
            <AnimatePresence initial={false} data-id="element-164" data-mp="3i1iwylad44jb">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-sky-100 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-165" data-mp="3e1i96a1digzl">
                  {answer}
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
const costs = [[ClockIcon, 'Otro año con dolor', 'Cada mes que pasa sin trabajar movilidad y core, la rigidez y la debilidad se profundizan. El tiempo no cura una espalda que no entrena.'], [AlertCircleIcon, 'Sesiones sueltas que no acumulan', 'Una sesión de kinesiología cuesta más que todo el combo y sin un plan diario en casa el efecto se pierde en 48 horas.'], [FrownIcon, 'Rutinas abandonadas', 'Sin progresión ni seguimiento, el 80% abandona antes del mes. Con el calendario y las fichas sabés exactamente qué toca cada semana.'], [ShieldAlertIcon, 'Miedo a moverte', 'Sin criterios claros, cada ejercicio genera dudas. El Sistema de Semáforo te dice cuándo avanzar, cuándo sostener y cuándo frenar.']] as const;
export function GuaranteeAndCost() {
  return <>
      <section className="border-y border-sky-100 bg-sky-50/70 px-4 py-12 md:py-16" data-id="element-166" data-mp="2dq72k2njnhks">
        <div className="mx-auto max-w-3xl text-center" data-id="element-167" data-mp="2inz3tna2wg1n">
          <span className="inline-flex rounded-full border-2 border-primary bg-sky-100 p-5 shadow-lg shadow-sky-100" data-id="element-168" data-mp="24p97i9v32kec">
            <ShieldIcon className="h-10 w-10 text-accent" data-id="element-169" data-mp="28sfan8mwfvgl" />
          </span>
          <Eyebrow data-id="element-170" data-mp="1ururgbd2nxmk">🛡️ Sin riesgo · 100% garantía</Eyebrow>
          <SectionTitle data-id="element-171" data-mp="1utfktxnnhaiu">
            Probalo 7 días. Si no te sirve,{' '}
            <span className="text-accent" data-id="element-172" data-mp="1zrh3wil2t9ub">te devolvemos el 100%.</span>
          </SectionTitle>
          <div className="mt-7 rounded-2xl border border-sky-100 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-173" data-mp="3p6niry6xa28p">
            <p data-id="element-174" data-mp="3u2coykdg6hdz">
              Probalo una semana completa.{' '}
              <b className="text-slate-900" data-id="element-175" data-mp="1ii55f08kkhc7">Aplicás las rutinas en casa o en el gimnasio.</b>
            </p>
            <p className="mt-3" data-id="element-176" data-mp="1djtv6oed44tg">
              Y si sentís que no era lo que esperabas, escribís a compradigitalyaa@gmail.com y te
              devolvemos el dinero.
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-177" data-mp="1nfohbthsoi0z">
              {['Sin vueltas', 'Sin formularios', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-178" data-mp="1w99wvf1sa3bg">
                  <CheckCircle2Icon className="h-5 w-5 text-accent" data-id="element-179" data-mp="2b60y7tv0pp3p" />
                  {text}
                </span>)}
            </div>
          </div>
          <p className="mt-6 text-lg font-bold text-slate-900" data-id="element-180" data-mp="267h9xmsyc186">
            El riesgo es nuestro, <span className="text-accent" data-id="element-181" data-mp="gst09tbqwxdu">no tuyo.</span>
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-id="element-182" data-mp="btgbv4587i95">
            <Metric value="<1%" label="Tasa de devoluciones" data-id="element-183" data-mp="bv9msvmvgomy" />
            <Metric value="+500" label="Personas satisfechas" data-id="element-184" data-mp="11idlt9586qbm" />
            <Metric value="7 días" label="Garantía sin condiciones" data-id="element-185" data-mp="15i4clcur56g5" />
          </div>
        </div>
      </section>

      <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-186" data-mp="1ahh5ofc7v9r6">
        <div className="mx-auto max-w-4xl" data-id="element-187" data-mp="1fhd29til3ywp">
          <Eyebrow danger data-id="element-188" data-mp="1fiemoolhpxr3">⚖️ El costo real</Eyebrow>
          <SectionTitle data-id="element-189" data-mp="1kgg8hhaawb69">
            ¿Cuánto te cuesta <span className="text-red-500" data-id="element-190" data-mp="1kfetx2u2t6js">NO tenerlo?</span>
          </SectionTitle>
          <div className="mt-10 grid gap-3 sm:grid-cols-2" data-id="element-191" data-mp="1og75i2qf7nj8">
            {costs.map(([CostIcon, label, cost]) => <div key={label} className="flex gap-3 rounded-xl border border-sky-100 bg-sky-50/50 p-4 shadow-sm" data-id="element-192" data-mp="1t90b0stpqzwd">
                <span className="h-fit rounded-lg border border-red-100 bg-red-50 p-2" data-id="element-193" data-mp="2377eq9yzrlgf">
                  <CostIcon className="h-5 w-5 text-red-500" data-id="element-194" data-mp="284zhy0536tzx" />
                </span>
                <p data-id="element-195" data-mp="3sn9yrm3407yg">
                  <b className="block text-sm text-slate-900" data-id="element-196" data-mp="3snth33wo8yd0">{label}</b>
                  <span className="text-sm font-medium text-slate-500" data-id="element-197" data-mp="3nogkhrg00cx8">{cost}</span>
                </p>
              </div>)}
          </div>
          <p className="mt-10 rounded-2xl border border-primary/20 bg-white p-6 text-center text-lg font-bold text-slate-700 shadow-sm md:text-2xl" data-id="element-198" data-mp="3iq5cznd6eei6">
            La próxima semana puede ser igual.
            <br data-id="element-199" data-mp="f7c1rh7ve1r6" />
            <span className="text-accent" data-id="element-200" data-mp="f7vhcra1se21">O puede ser la primera con un sistema real.</span>
          </p>
          <div className="mt-8 text-center" data-id="element-201" data-mp="abn97xu9pl4d">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-202" data-mp="5ic1zvljn2bx">
              EMPEZAR HOY CON EL SISTEMA COMPLETO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
function Metric({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="rounded-xl border border-sky-100 bg-white p-3" data-id="element-203" data-mp="t7wi86qbblhi">
      <p className="font-display text-xl font-bold text-accent" data-id="element-204" data-mp="t6leu80s4fqx">{value}</p>
      <p className="mt-1 text-[10px] font-bold leading-tight text-slate-500" data-id="element-205" data-mp="dp3lrtkd768j">{label}</p>
    </div>;
}